import { t } from "../i18n/index.ts";
import type { IconName } from "./icons.js";

export const TAB_GROUPS = [
  { label: "chat", tabs: ["chat"] },
  {
    label: "control",
    tabs: ["overview", "channels", "instances", "sessions", "usage", "cron"],
  },
  { label: "agent", tabs: ["agents", "skills", "nodes"] },
  { label: "settings", tabs: ["config", "debug", "logs"] },
] as const;

export type Tab =
  | "agents"
  | "overview"
  | "channels"
  | "instances"
  | "sessions"
  | "usage"
  | "cron"
  | "skills"
  | "nodes"
  | "chat"
  | "config"
  | "debug"
  | "logs"
  | "contentforge"
  // Mission Control sidebar tabs
  | "mc-tasks"
  | "mc-content"
  | "mc-pinterest"
  | "mc-approvals"
  | "mc-council"
  | "mc-calendar"
  | "mc-projects"
  | "mc-memory"
  | "mc-products"
  | "mc-docs"
  | "mc-people"
  | "mc-office"
  | "mc-team"
  | "mc-usage"
  | "mc-settings";

// ─── Mission Control Sidebar Items ──────────────────────────────────────

export type McSidebarItem = {
  id: Tab;
  label: string;
  icon: IconName;
  /** If this MC tab wraps an existing OpenClaw tab, specify which */
  wrapsTab?: Tab;
  /** If true, render a divider before this item */
  dividerBefore?: boolean;
};

export const MC_SIDEBAR_ITEMS: McSidebarItem[] = [
  { id: "mc-tasks", label: "Tasks", icon: "fileText" },
  { id: "mc-content", label: "Content", icon: "penLine" },
  { id: "mc-pinterest", label: "Pinterest", icon: "image" },
  { id: "mc-approvals", label: "Approvals", icon: "checkCircle" },
  { id: "mc-council", label: "Council", icon: "messageSquare", wrapsTab: "chat" },
  { id: "mc-calendar", label: "Calendar", icon: "calendar" },
  { id: "mc-projects", label: "Projects", icon: "folder" },
  { id: "mc-memory", label: "Memory", icon: "brain" },
  { id: "mc-products", label: "Products", icon: "package" },
  { id: "mc-docs", label: "Docs", icon: "book" },
  { id: "mc-people", label: "People", icon: "users", wrapsTab: "channels" },
  { id: "mc-office", label: "Office", icon: "monitor" },
  { id: "mc-team", label: "Team", icon: "users" },
  { id: "mc-usage", label: "Usage", icon: "barChart", wrapsTab: "usage" },
  {
    id: "mc-settings",
    label: "Settings",
    icon: "settings",
    dividerBefore: true,
  },
];

/** Check if a tab belongs to Mission Control */
export function isMcTab(tab: Tab): boolean {
  return tab.startsWith("mc-") || tab === "contentforge";
}

/** Get the default MC tab when entering Mission Control */
export function getDefaultMcTab(): Tab {
  return "mc-tasks";
}

/** Get the actual OpenClaw tab to render for an MC tab (for wrapped views) */
export function getWrappedTab(mcTab: Tab): Tab | null {
  const item = MC_SIDEBAR_ITEMS.find((i) => i.id === mcTab);
  return item?.wrapsTab ?? null;
}

const TAB_PATHS: Record<Tab, string> = {
  agents: "/agents",
  overview: "/overview",
  channels: "/channels",
  instances: "/instances",
  sessions: "/sessions",
  usage: "/usage",
  cron: "/cron",
  skills: "/skills",
  nodes: "/nodes",
  chat: "/chat",
  config: "/config",
  debug: "/debug",
  logs: "/logs",
  contentforge: "/contentforge",
  "mc-tasks": "/tasks",
  "mc-content": "/content",
  "mc-pinterest": "/pinterest",
  "mc-approvals": "/approvals",
  "mc-council": "/council",
  "mc-calendar": "/calendar",
  "mc-projects": "/projects",
  "mc-memory": "/memory",
  "mc-products": "/products",
  "mc-docs": "/docs",
  "mc-people": "/people",
  "mc-office": "/office",
  "mc-team": "/team",
  "mc-usage": "/mc-usage",
  "mc-settings": "/settings",
};

const PATH_TO_TAB = new Map(Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]));

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    // Default landing page is Mission Control Tasks
    return "mc-tasks";
  }
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  // Check MC sidebar items first
  const mcItem = MC_SIDEBAR_ITEMS.find((i) => i.id === tab);
  if (mcItem) {
    return mcItem.icon;
  }
  switch (tab) {
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "overview":
      return "barChart";
    case "channels":
      return "link";
    case "instances":
      return "radio";
    case "sessions":
      return "fileText";
    case "usage":
      return "barChart";
    case "cron":
      return "loader";
    case "skills":
      return "zap";
    case "nodes":
      return "monitor";
    case "config":
      return "settings";
    case "debug":
      return "bug";
    case "logs":
      return "scrollText";
    case "contentforge":
      return "zap";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  return t(`tabs.${tab}`);
}

export function subtitleForTab(tab: Tab) {
  return t(`subtitles.${tab}`);
}
